/* ==========================================================================
   DreamBrothers Live Configurator — db-configurator.js
   Vanilla JS custom element. Depends on db-glb.js (window.DBGlb).

   Flow: "Shop by Dream" cards → /pages/dream-customizer?goal=xxx → this
   section auto-opens with the right product (fetched via /products/X.js),
   the right mode (wall-art | apparel) and goal-aligned presets (phrases +
   design images). Also embeddable on any product page as a trigger banner.
   ========================================================================== */
(function () {
  'use strict';

  if (customElements.get('db-configurator')) return;

  /* ------------------------------------------------------------------ *
   * Helpers
   * ------------------------------------------------------------------ */

  function csvPairs(list, fallbackValue) {
    // "Label|value" — the LAST pipe splits label from value, so labels may
    // themselves contain pipes (Gelato variants look like "S | 30x40 cm").
    return (list || [])
      .map(function (raw) {
        var parts = String(raw).trim().split('|');
        var label, value;
        if (parts.length > 1) {
          value = parts.pop().trim();
          label = parts.join('|').trim();
        } else {
          label = parts[0].trim();
          value = (fallbackValue || label).trim();
        }
        if (!label) return null;
        return { label: label, value: value || fallbackValue || label };
      })
      .filter(Boolean);
  }

  function formatMoney(cents, currency) {
    try {
      var locale = document.documentElement.lang || undefined;
      return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2) + ' ' + currency;
    }
  }

  function el(tag, className, html) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (html != null) node.innerHTML = html;
    return node;
  }

  // Text-safe element builder: never interprets content as HTML.
  function elText(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  // Only allow hex / rgb() / hsl() / named colors into a CSS custom property.
  function safeColor(v) {
    v = String(v || '').trim();
    if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(v)) return v;
    if (/^rgb\(\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+\s*\)$/.test(v)) return v;
    if (/^rgba\(\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+\s*\)$/.test(v)) return v;
    if (/^hsl\(\s*[\d.]+\s*,\s*[\d.]+%\s*,\s*[\d.]+%\s*\)$/.test(v)) return v;
    if (/^[a-zA-Z]{3,20}$/.test(v)) return v; // named colors
    return '#333333';
  }

  // Only allow http(s)/protocol-relative/root-relative/data:image URLs.
  function safeImageUrl(v) {
    v = String(v || '').trim();
    if (/^(https?:)?\/\//i.test(v)) return v;
    if (/^\//.test(v)) return v;
    if (/^data:image\//i.test(v)) return v;
    return '';
  }

  // Markets translate option VALUES into inches ("L | 30x40 in" is 75x100 cm).
  // Every size comparison goes through a canonical cm key so a selection made
  // in one market still resolves the right variant.
  // Mirrors the storefront size guides (portrait / square / landscape + A4).
  var INCH_TO_CM = {
    '8x12': '21x30', '12x8': '30x21',
    // square
    '12x12': '30x30', '20x20': '50x50', '28x28': '70x70', '40x40': '100x100',
    // portrait
    '12x16': '30x40', '20x28': '50x70', '30x40': '75x100', '40x55': '100x140', '40x56': '100x140',
    // landscape
    '16x12': '40x30', '24x18': '60x45', '40x30': '100x75', '55x40': '140x100',
    // legacy
    '16x20': '40x50', '16x24': '40x60', '18x24': '45x60', '24x32': '60x80', '24x36': '60x90'
  };

  function labelIsInches(label) {
    var s = String(label || '').toLowerCase();
    return /\d\s*[x×]\s*\d+\s*(in\b|inch|")/.test(s) || /["”″]/.test(s) || /\binch(es)?\b/.test(s);
  }

  // "S | 30x40 cm" -> "30x40"  |  "L | 30x40 in" -> "75x100"
  // The unit must be the one ATTACHED to the number pair: a label carrying both
  // ("30x40 cm / 12x16\"") would otherwise be read as inches and 30x40 cm would
  // silently become 75x100.
  function sizeCmKey(label) {
    var s = String(label || '').toLowerCase();
    var re = /(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)\s*(cm|mm|centimetri|in\b|inch(?:es)?|"|”|″)?/g;
    var m, cmPair = null, inPair = null, firstPair = null;
    while ((m = re.exec(s)) !== null) {
      var a = parseFloat(m[1].replace(',', '.')), b = parseFloat(m[2].replace(',', '.'));
      if (!a || !b) continue;
      var pair = Math.round(a) + 'x' + Math.round(b);
      var unit = (m[3] || '').trim();
      if (!firstPair) firstPair = pair;
      if (/^(cm|mm|centimetri)$/.test(unit)) { if (!cmPair) cmPair = pair; }
      else if (unit) { if (!inPair) inPair = pair; }
    }
    if (cmPair) return cmPair;                       // an explicit cm pair wins
    if (inPair) {
      if (INCH_TO_CM[inPair]) return INCH_TO_CM[inPair];
      var p = inPair.split('x');
      return Math.round(parseFloat(p[0]) * 2.54) + 'x' + Math.round(parseFloat(p[1]) * 2.54);
    }
    return firstPair || '';
  }

  // "S | 30x40 cm" -> "30x40"  (the cm pair drives the preview aspect ratio)
  function sizeValueFromLabel(label) {
    var k = sizeCmKey(label);
    if (k) return k;
    return String(label || '');
  }

  // Normalized frame key: "Without Frame"/"No frame"/"Senza cornice" -> 'none',
  // "Black Frame" -> 'black', … Used to match state against real option values.
  function frameKeyFromLabel(label) {
    var f = String(label || '').toLowerCase().trim();
    if (!f || /\b(no|none|without|senza|nessun\w*)\b/.test(f)) return 'none';
    if (f.indexOf('white') >= 0 || f.indexOf('bianc') >= 0) return 'white';
    if (f.indexOf('black') >= 0 || f.indexOf('ner') >= 0) return 'black';
    if (f.indexOf('oak') >= 0 || f.indexOf('rovere') >= 0) return 'oak';
    if (f.indexOf('walnut') >= 0 || f.indexOf('noce') >= 0) return 'walnut';
    return f;
  }

  // Loose equality between a state selection and a variant option value:
  // exact match, same size pair ("50x70 cm" == "M | 50x70 cm") or same frame
  // family ("Black" == "Black Frame"). Keeps price + variant in sync even when
  // labels come from different sources (CSV settings vs real options).
  function optionMatches(stateVal, optVal) {
    var a = String(stateVal || '').trim();
    var b = String(optVal || '').trim();
    if (!a || !b) return false;
    if (a.toLowerCase() === b.toLowerCase()) return true;
    // Sizes compare on the canonical cm key (unit- and market-proof).
    var sa = sizeCmKey(a), sb = sizeCmKey(b);
    if (sa && sb) return sa === sb;
    if (sa || sb) return false; // one is a size, the other isn't
    return frameKeyFromLabel(a) === frameKeyFromLabel(b);
  }
  // "Black Frame" -> hex swatch; "Without Frame" -> none
  function frameHexFromLabel(label) {
    var f = String(label || '').toLowerCase();
    if (!f || /\b(no|none|without|senza|nessun\w*)\b/.test(f)) return 'none';
    if (f.indexOf('white') >= 0 || f.indexOf('bianc') >= 0) return '#f4f2ee';
    if (f.indexOf('black') >= 0 || f.indexOf('ner') >= 0) return '#15151a';
    if (f.indexOf('oak') >= 0 || f.indexOf('rovere') >= 0) return '#c8a165';
    if (f.indexOf('walnut') >= 0 || f.indexOf('noce') >= 0) return '#6b4a2f';
    return '#15151a';
  }

  function urlParam(name) {
    try { return new URLSearchParams(window.location.search).get(name); } catch (e) { return null; }
  }

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  /* ---- 3D flip on frame change (280 = durata di db-flip-out/in nel CSS) */
  var FLIP_HALF_MS = 280;
  var FLIP_STEPS = ['frame']; // aggiungi 'material' o 'border' per il flip anche lì

  var CAMERA_PRESETS = {
    design: { x: 0, y: 0, s: 1.08 },
    size: { x: 0, y: 0, s: 1 },
    color: { x: 0, y: 0, s: 1.12 },
    frame: { x: 12, y: 10, s: 1.7 },
    material: { x: 0, y: 0, s: 1.25 },
    finish: { x: 0, y: 2, s: 1.42 },
    thickness: { x: 14, y: 6, s: 1.55 },
    border: { x: -12, y: 10, s: 1.7 },
    placement: { x: 0, y: 0, s: 1.1 },
    text: { x: 0, y: -14, s: 1.6 },
    upload: { x: 0, y: 0, s: 1.2 }
  };

  var TEXT_COLORS = ['#ffffff', '#0b0d12', '#0075e3', '#e63946'];

  var TEXT_FONTS = [
    { id: 'default', label: 'Bold' },
    { id: 'serif', label: 'Serif' },
    { id: 'script', label: 'Script' }
  ];

  var FALLBACK_LABELS = {
    design: 'Design', upload: 'Your image',
    uploadNote: 'PNG or JPG, max 8 MB. Our team confirms print quality before production.',
    uploadCta: 'Tap to choose an image',
    addLayer: '+ Add text', removeLayer: 'Remove', background: 'Background',
    saving: 'Saving your design…', personalize: 'Personalize design',
    inlineTitle: 'Personalize it', openEditor: 'Open the full editor'
  };

  var BG_COLORS = ['', '#ffffff', '#f7f0e3', '#0b0d12', '#e8d9c0'];

  var TEXT_SIZES = [
    { id: 0.75, label: 'S' },
    { id: 1, label: 'M' },
    { id: 1.35, label: 'L' }
  ];

  // Print export caps: long side + total area (iOS Safari canvas limit ~16.7MP).
  var PRINT_MAX_SIDE = 4096;
  var PRINT_MAX_AREA = 16000000;

  function fontCss(fontId, px) {
    return fontId === 'serif' ? '600 ' + px + 'px Georgia, serif'
      : fontId === 'script' ? '500 ' + px + 'px "Segoe Script", "Brush Script MT", cursive'
        : 'italic 800 ' + px + 'px Montserrat, Poppins, sans-serif';
  }

  /* Battle-tested placement (the n8n "fitMethod: meet" method): the design is
     CONTAIN-fitted in the print area — never cropped, never stretched. The
     leftover bands are filled with MIRRORED continuations of the artwork
     (specchiatura dei bordi), which also gives canvases seamless wrap edges. */
  function drawDesignMeet(ctx, img, ax, ay, aw, ah) {
    var scale = Math.min(aw / img.width, ah / img.height);
    var dw = img.width * scale, dh = img.height * scale;
    var dx = ax + (aw - dw) / 2, dy = ay + (ah - dh) / 2;
    ctx.save();
    ctx.beginPath();
    ctx.rect(ax, ay, aw, ah);
    ctx.clip();
    ctx.drawImage(img, dx, dy, dw, dh);
    if (dx > ax + 0.5) {
      ctx.save(); ctx.translate(dx, 0); ctx.scale(-1, 1);
      ctx.drawImage(img, 0, dy, dw, dh);          // left mirror band
      ctx.restore();
      ctx.save(); ctx.translate(dx + dw, 0); ctx.scale(-1, 1);
      ctx.drawImage(img, -dw, dy, dw, dh);        // right mirror band
      ctx.restore();
    }
    if (dy > ay + 0.5) {
      ctx.save(); ctx.translate(0, dy); ctx.scale(1, -1);
      ctx.drawImage(img, dx, 0, dw, dh);          // top mirror band
      ctx.restore();
      ctx.save(); ctx.translate(0, dy + dh); ctx.scale(1, -1);
      ctx.drawImage(img, dx, -dh, dw, dh);        // bottom mirror band
      ctx.restore();
    }
    ctx.restore();
    return { dx: dx, dy: dy, dw: dw, dh: dh };
  }

  /* ------------------------------------------------------------------ *
   * Custom element
   * ------------------------------------------------------------------ */

  function DBConfigurator() {
    return Reflect.construct(HTMLElement, [], DBConfigurator);
  }
  DBConfigurator.prototype = Object.create(HTMLElement.prototype);
  DBConfigurator.prototype.constructor = DBConfigurator;

  DBConfigurator.prototype.connectedCallback = function () {
    var cfgNode = this.querySelector('[data-db-config]');
    if (!cfgNode) return;
    try {
      this.cfg = JSON.parse(cfgNode.textContent);
    } catch (e) {
      console.error('[db-configurator] invalid config JSON', e);
      return;
    }

    // Normalize presets coming from Liquid textareas (newline artifacts).
    (this.cfg.presets || []).forEach(function (p) {
      p.phrases = (p.phrases || []).map(function (s) { return String(s).trim(); }).filter(Boolean);
      p.images = (p.images || []).map(function (s) { return String(s).trim(); }).filter(Boolean);
    });

    // Personalization config (Gelato Personalization Studio parity).
    this.pz = this.cfg.personalization || {};
    this.pz.textLayers = Array.isArray(this.pz.textLayers) ? this.pz.textLayers : [];
    this.workerUrl = String(this.pz.workerUrl || '').replace(/\/+$/, '');

    this.overlay = null;
    this.activePreset = this.resolvePreset();
    this.ready = this.resolveProduct();

    this.storageKey = 'db-cfg:' + (this.targetHandle() || 'product');
    this.state = this.defaultState();
    this.restoreState();

    var open = this.querySelector('[data-db-open]');
    var self = this;
    if (open) open.addEventListener('click', function () { self.open(); });

    // Warm up the fulfillment worker check so add-to-cart never waits for a
    // dead endpoint (result cached in sessionStorage for 10 minutes).
    this.pingWorker();

    var inThemeEditor = !!(window.Shopify && window.Shopify.designMode);
    if (this.pz.type === 'inpage') {
      // Gelato "Shopify in-page personalization": fields + live preview live
      // directly on the product page, no overlay needed to buy.
      this.ready.then(function () { self.mountInline(); });
    }
    if (this.cfg.autoOpen && !inThemeEditor) {
      // Dedicated customizer page: Porsche-style instant takeover.
      // Skipped in the theme editor so the merchant isn't trapped in the overlay.
      this.ready.then(function () { self.open(); });
    }
  };

  /* ------------------------- goal presets --------------------------- */

  DBConfigurator.prototype.resolvePreset = function () {
    var goal = (urlParam('goal') || '').toLowerCase();
    var presets = this.cfg.presets || [];
    if (!goal) return null;
    for (var i = 0; i < presets.length; i++) {
      if (String(presets[i].id || '').toLowerCase() === goal) return presets[i];
    }
    return null;
  };

  DBConfigurator.prototype.modeNow = function () {
    return (this.activePreset && this.activePreset.mode) || this.cfg.mode;
  };

  DBConfigurator.prototype.targetHandle = function () {
    return urlParam('product') ||
      (this.activePreset && this.activePreset.productHandle) ||
      this.cfg.productHandle;
  };

  /* Fetch the target product client-side when it differs from the one the
     section was rendered with (active products only — Shopify AJAX API). */
  DBConfigurator.prototype.resolveProduct = function () {
    var self = this;
    var handle = this.targetHandle();
    if (!handle || handle === this.cfg.productHandle) return Promise.resolve();

    return fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('product fetch ' + r.status);
        return r.json();
      })
      .then(function (p) {
        self.cfg.productId = p.id;
        self.cfg.productTitle = p.title;
        self.cfg.productHandle = p.handle;
        self.cfg.featuredImage = p.featured_image || (p.images && p.images[0]) || self.cfg.featuredImage;
        self.cfg.optionNames = (p.options || []).map(function (o) { return typeof o === 'string' ? o : o.name; });
        self.cfg.variants = (p.variants || []).map(function (v) {
          return {
            id: v.id,
            title: v.title,
            options: v.options || [v.option1, v.option2, v.option3].filter(function (x) { return x != null; }),
            price: v.price,
            available: v.available,
            image: v.featured_image ? v.featured_image.src : null
          };
        });
        self.state = self.defaultState();
        self.restoreState();
      })
      .catch(function (err) {
        console.warn('[db-configurator] falling back to section product:', err.message);
      });
  };

  /* Is the customer currently configuring a canvas? (drives the wrap bleed and
     the thickness step, which only exists for canvases). */
  DBConfigurator.prototype.isCanvasSelected = function () {
    return /canvas|tela|leinwand/i.test(String(this.state.material || '') || String(this.cfg.productTitle || ''));
  };

  /* Which mapped material corresponds to the product currently loaded? */
  DBConfigurator.prototype.materialLabelForLoadedProduct = function () {
    var mp = (this.cfg.materialProducts || []).filter(function (m) { return m && m.label; });
    if (!mp.length) return '';
    var handle = this.cfg.productHandle;
    for (var i = 0; i < mp.length; i++) if (mp[i].handle === handle) return mp[i].label;
    // Not mapped explicitly: infer from the product title so the preview still
    // knows whether it is composing a canvas (wrap bleed) or a poster.
    var t = String(this.cfg.productTitle || '').toLowerCase();
    for (var k = 0; k < mp.length; k++) {
      if (t.indexOf(String(mp[k].label).toLowerCase()) > -1) return mp[k].label;
    }
    return mp[0].label;
  };

  /* Swap the underlying Shopify product when the customer changes material.
     Prices/sizes/availability always come from Shopify (correct per market and
     currency) instead of a duplicated price table in the section settings. */
  DBConfigurator.prototype.switchMaterialProduct = function (handle) {
    var self = this;
    if (!handle || handle === this.cfg.productHandle) {
      this.renderAll({ changed: 'material' });
      return Promise.resolve();
    }
    var keepSize = this.state.size, keepFrame = this.state.frame;
    var priceEl = this.overlay && this.overlay.querySelector('[data-db-price]');
    if (priceEl) priceEl.classList.add('is-loading');

    // Tapping Poster then Canvas quickly used to let whichever response landed
    // last win, desyncing cfg from the visible selection (and from the price).
    // Only the most recent request is allowed to apply.
    var token = (this._switchToken = (this._switchToken || 0) + 1);

    return fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('product fetch ' + r.status);
        return r.json();
      })
      .then(function (p) {
        if (token !== self._switchToken) return; // superseded by a later tap
        self.cfg.productId = p.id;
        self.cfg.productTitle = p.title;
        self.cfg.productHandle = p.handle;
        self.cfg.featuredImage = p.featured_image || (p.images && p.images[0]) || self.cfg.featuredImage;
        self.cfg.optionNames = (p.options || []).map(function (o) { return typeof o === 'string' ? o : o.name; });
        self.cfg.variants = (p.variants || []).map(function (v) {
          return {
            id: v.id,
            title: v.title,
            options: v.options || [v.option1, v.option2, v.option3].filter(function (x) { return x != null; }),
            price: v.price,
            available: v.available,
            image: v.featured_image ? v.featured_image.src : null
          };
        });
        // Carry the customer's size/frame across the swap (normalizeSelections
        // maps them onto the new product's real option values, cm/inch aware).
        self.state.size = keepSize;
        self.state.frame = keepFrame;
        self.normalizeSelections();
        self.saveState();
        self.steps = self.buildSteps();
        self.buildTabs();
        self.buildPanes();
        var titleEl = self.overlay && self.overlay.querySelector('[data-db-title]');
        if (titleEl) titleEl.textContent = self.cfg.productTitle;
        self.stepIndex = Math.min(self.stepIndex, self.steps.length - 1);
        self.renderAll({ changed: 'material' });
      })
      .catch(function (err) {
        if (token !== self._switchToken) return;
        console.warn('[db-configurator] material switch failed:', err.message);
        self.renderAll({ changed: 'material' });
      })
      .then(function () {
        if (token === self._switchToken && priceEl) priceEl.classList.remove('is-loading');
      });
  };

  /* ---------------------------- state ------------------------------- */

  DBConfigurator.prototype.defaultState = function () {
    var cfg = this.cfg;
    var sizes = csvPairs(cfg.steps.size.values);
    var frames = csvPairs(cfg.steps.frame.values, 'none');
    var materials = csvPairs(cfg.steps.material.values);
    var finishes = csvPairs(cfg.steps.finish.values);
    var borders = csvPairs(cfg.steps.border.values, '0');
    var placements = csvPairs(cfg.steps.placement.values);
    var preset = this.activePreset;

    // Real variant option values (when the product has them) beat the CSV
    // settings: the selection must always land on a purchasable variant.
    var realSizes = this.optionValues(cfg.sizeOptionName);
    var realFrames = this.optionValues(cfg.frameOptionName || 'Frame');

    return {
      size: realSizes.length ? (realSizes[1] || realSizes[0])
        : (sizes[1] ? sizes[1].label : (sizes[0] ? sizes[0].label : '')),
      color: '',
      frame: realFrames.length ? realFrames[0]
        : (frames[0] ? frames[0].label : ''),
      // The material must describe the product actually loaded, not the first
      // entry of an unrelated CSV: getting this wrong meant a canvas order was
      // composed without the wrap bleed (design cropped on the stretcher).
      material: this.materialLabelForLoadedProduct()
        || (materials[0] ? materials[0].label : ''),
      finish: finishes[0] ? finishes[0].label : '',
      thickness: csvPairs((cfg.steps.thickness || {}).values || [])[0]
        ? csvPairs(cfg.steps.thickness.values)[0].label : '',
      border: borders[0] ? borders[0].label : '',
      placement: placements[0] ? placements[0].label : '',
      designImage: preset && preset.images && preset.images[0] ? preset.images[0] : '',
      designLabel: '',
      uploadDataUrl: '',
      uploadName: '',
      bgColor: '',
      activeLayer: 0,
      layers: this.buildDefaultLayers(preset)
    };
  };

  /* Text layers: one per "Personalized text layer" block (Gelato-style, with
     allow-personalization + sample-element). Falls back to a single implicit
     layer when the merchant hasn't defined blocks (legacy behavior). */
  DBConfigurator.prototype.buildDefaultLayers = function (preset) {
    var defs = (this.pz && this.pz.textLayers && this.pz.textLayers.length) ? this.pz.textLayers : null;
    var presetText = preset && preset.phrases && preset.phrases[0] ? preset.phrases[0] : '';
    if (!defs) {
      if (!(this.cfg.steps.text && this.cfg.steps.text.enabled)) return [];
      return [{
        key: 'text1', label: this.label('text'), text: presetText, defaultText: '',
        color: TEXT_COLORS[0], font: 'default', pos: 'bottom', sizeMul: 1,
        allow: true, sample: false, maxChars: (this.cfg.steps.text && this.cfg.steps.text.maxChars) || 40,
        enabled: true, changed: !!presetText, removable: false
      }];
    }
    return defs.map(function (d, i) {
      return {
        key: String(d.key || ('layer' + i)),
        label: String(d.label || ('Text ' + (i + 1))),
        text: (i === 0 && presetText) ? presetText : String(d.defaultText || ''),
        defaultText: String(d.defaultText || ''),
        color: safeColor(d.color || TEXT_COLORS[0]),
        font: d.font === 'serif' || d.font === 'script' ? d.font : 'default',
        pos: d.pos === 'top' || d.pos === 'center' ? d.pos : 'bottom',
        sizeMul: 1,
        allow: d.allow !== false,
        sample: !!d.sample,
        maxChars: parseInt(d.maxChars, 10) || 40,
        enabled: true,
        changed: i === 0 && !!presetText,
        removable: false
      };
    });
  };

  DBConfigurator.prototype.activeLayerObj = function () {
    var layers = this.state.layers || [];
    return layers[this.state.activeLayer] || layers[0] || null;
  };

  DBConfigurator.prototype.restoreState = function () {
    try {
      var saved = JSON.parse(localStorage.getItem(this.storageKey) || 'null');
      if (saved && typeof saved === 'object') {
        for (var k in this.state) {
          if (saved[k] != null && k !== 'uploadDataUrl' && k !== 'layers') this.state[k] = saved[k];
        }
        var layers = this.state.layers || [];
        if (Array.isArray(saved.layers)) {
          // Merge by key so merchant block edits win over stale storage.
          saved.layers.forEach(function (sl) {
            if (!sl || !sl.key) return;
            for (var i = 0; i < layers.length; i++) {
              if (layers[i].key === sl.key) {
                if (typeof sl.text === 'string') layers[i].text = sl.text.slice(0, layers[i].maxChars);
                if (sl.color) layers[i].color = safeColor(sl.color);
                if (sl.font) layers[i].font = sl.font;
                if (sl.pos) layers[i].pos = sl.pos;
                if (sl.sizeMul) layers[i].sizeMul = sl.sizeMul;
                if (typeof sl.enabled === 'boolean') layers[i].enabled = sl.enabled;
                if (typeof sl.changed === 'boolean') layers[i].changed = sl.changed;
                return;
              }
            }
            // Customer-added layer from a previous visit.
            if (sl.removable && typeof sl.text === 'string') {
              layers.push({
                key: String(sl.key), label: String(sl.label || 'Text'), text: sl.text,
                defaultText: '', color: safeColor(sl.color || TEXT_COLORS[0]),
                font: sl.font || 'default', pos: sl.pos || 'center', sizeMul: sl.sizeMul || 1,
                allow: true, sample: false, maxChars: 40, enabled: sl.enabled !== false,
                changed: true, removable: true
              });
            }
          });
        } else if (typeof saved.text === 'string' && saved.text && layers[0]) {
          // Legacy single-text storage migration.
          layers[0].text = saved.text.slice(0, layers[0].maxChars);
          layers[0].changed = true;
          if (saved.textColor) layers[0].color = safeColor(saved.textColor);
          if (saved.textFont) layers[0].font = saved.textFont;
          if (saved.textPos) layers[0].pos = saved.textPos;
        }
        if (this.state.activeLayer >= layers.length) this.state.activeLayer = 0;
        this.state.bgColor = this.state.bgColor ? safeColor(this.state.bgColor) : '';
      }
    } catch (e) { /* ignore */ }
    this.normalizeSelections();
  };

  /* Map stale/CSV-era selections onto the product's REAL option values, so a
     saved "50x70 cm" becomes "M | 50x70 cm" and always resolves a variant. */
  DBConfigurator.prototype.normalizeSelections = function () {
    var cfg = this.cfg;
    var realSizes = this.optionValues(cfg.sizeOptionName);
    if (realSizes.length && realSizes.indexOf(this.state.size) === -1) {
      var self = this;
      var hit = realSizes.filter(function (s) { return optionMatches(self.state.size, s); })[0];
      this.state.size = hit || realSizes[1] || realSizes[0];
    }
    var realFrames = this.optionValues(cfg.frameOptionName || 'Frame');
    if (realFrames.length && realFrames.indexOf(this.state.frame) === -1) {
      var self2 = this;
      var fhit = realFrames.filter(function (f) { return optionMatches(self2.state.frame, f); })[0];
      this.state.frame = fhit || realFrames[0];
    }
  };

  DBConfigurator.prototype.saveState = function () {
    try {
      var copy = Object.assign({}, this.state);
      delete copy.uploadDataUrl; // too big for localStorage
      localStorage.setItem(this.storageKey, JSON.stringify(copy));
    } catch (e) { /* ignore */ }
  };

  DBConfigurator.prototype.label = function (id) {
    return (this.cfg.labels && this.cfg.labels[id]) || FALLBACK_LABELS[id] || id;
  };

  /* ---------------------------- steps ------------------------------- */

  DBConfigurator.prototype.buildSteps = function () {
    var cfg = this.cfg;
    var mode = this.modeNow();
    var steps = [];
    var preset = this.activePreset;

    if (preset && ((preset.images && preset.images.length) || (preset.phrases && preset.phrases.length))) {
      steps.push({ id: 'design' });
    }

    if (mode === 'apparel') {
      var colorValues = this.optionValues(cfg.colorOptionName);
      if (colorValues.length) steps.push({ id: 'color', values: colorValues.map(function (c) { return { label: c, value: c }; }) });
      var sizeValues = this.optionValues(cfg.sizeOptionName);
      steps.push({
        id: 'size',
        values: sizeValues.length
          ? sizeValues.map(function (s) { return { label: s, value: s }; })
          : csvPairs(cfg.steps.size.values)
      });
      if (cfg.steps.placement.enabled) steps.push({ id: 'placement', values: csvPairs(cfg.steps.placement.values) });
    } else {
      // Sizes/frames come from the product's REAL variant options when they
      // exist, so the customer's choice always selects (and pays for) the
      // matching variant. The section CSV settings are only a fallback for
      // products without those options.
      var realSizes = this.optionValues(cfg.sizeOptionName);
      steps.push({
        id: 'size',
        values: realSizes.length
          ? realSizes.map(function (s) { return { label: s, value: sizeValueFromLabel(s) }; })
          : csvPairs(cfg.steps.size.values)
      });
      var realFrames = this.optionValues(cfg.frameOptionName || 'Frame');
      if (realFrames.length) {
        steps.push({ id: 'frame', values: realFrames.map(function (f) { return { label: f, value: frameHexFromLabel(f) }; }) });
      } else if (cfg.steps.frame.enabled) {
        steps.push({ id: 'frame', values: csvPairs(cfg.steps.frame.values, 'none') });
      }
      // Material is NOT a variant option: Canvas and Poster are DIFFERENT
      // Shopify products with their own price lists. When the merchant maps
      // them ("Material product" blocks) each choice carries its product
      // handle, so picking a material swaps the whole product and the prices
      // come from Shopify — never from hardcoded settings.
      var matProducts = (cfg.materialProducts || []).filter(function (m) { return m && m.label; });
      if (matProducts.length) {
        steps.push({
          id: 'material',
          values: matProducts.map(function (m) { return { label: m.label, value: m.label, handle: m.handle }; })
        });
      } else if (cfg.steps.material.enabled) {
        var realMaterials = this.optionValues(cfg.materialOptionName);
        steps.push({
          id: 'material',
          values: realMaterials.length
            ? realMaterials.map(function (m) { return { label: m, value: m }; })
            : csvPairs(cfg.steps.material.values)
        });
      }
      // Spessore tela: solo per le tele (Gelato/Prodigi lo offrono solo li').
      // 19mm e 38mm sono prodotti diversi con costo diverso lato fornitore.
      if (cfg.steps.thickness && cfg.steps.thickness.enabled && this.isCanvasSelected()) {
        steps.push({ id: 'thickness', values: csvPairs(cfg.steps.thickness.values) });
      }
      if (cfg.steps.finish.enabled) steps.push({ id: 'finish', values: csvPairs(cfg.steps.finish.values) });
      if (cfg.steps.border.enabled) steps.push({ id: 'border', values: csvPairs(cfg.steps.border.values, '0') });
    }
    var anyEditableLayer = (this.state.layers || []).some(function (L) { return L.allow; });
    if (anyEditableLayer || this.pz.allowAddLayers) steps.push({ id: 'text' });
    if (cfg.steps.upload && cfg.steps.upload.enabled) steps.push({ id: 'upload' });
    return steps;
  };

  DBConfigurator.prototype.optionValues = function (optionName) {
    var idx = this.optionIndex(optionName);
    if (idx === -1) return [];
    var seen = [];
    this.cfg.variants.forEach(function (v) {
      var val = v.options[idx];
      if (val && seen.indexOf(val) === -1) seen.push(val);
    });
    return seen;
  };

  /* --------------------------- variants ----------------------------- */

  DBConfigurator.prototype.optionIndex = function (name) {
    var lower = String(name || '').toLowerCase();
    var idx = (this.cfg.optionNames || []).findIndex(function (n) { return String(n).toLowerCase() === lower; });
    if (idx > -1) return idx;
    // Markets translate option NAMES too ("Size" -> "Dimensione", "Frame" ->
    // "Cornice"). Matching on the English setting then silently returns -1,
    // which empties the variant criteria and pins every customer to the first
    // variant (wrong price, wrong frame). Detect the option by the SHAPE of
    // its values instead — that survives any translation.
    return this.detectOptionIndex(lower);
  };

  DBConfigurator.prototype.detectOptionIndex = function (kind) {
    var cfg = this.cfg;
    var names = cfg.optionNames || [];
    var tests = null;
    if (/size|dimens|taglia|misura|format|grosse|gr(ö|oe)/.test(kind)) {
      tests = function (vals) { return vals.every(function (v) { return /\d+\s*[x×]\s*\d+/.test(v); }); };
    } else if (/frame|cornice|telaio|rahmen|marco|cadre/.test(kind)) {
      tests = function (vals) { return vals.every(function (v) { return /frame|cornice|telaio|rahmen|marco|cadre|senza|without|sin |sans /i.test(v); }); };
    } else if (/material|materiale|materia/.test(kind)) {
      tests = function (vals) { return vals.every(function (v) { return /canvas|tela|leinwand|poster|paper|carta|papier|plexi|acrylic|acrilic/i.test(v); }); };
    } else if (/colou?r|colore|farbe|couleur|kleur/.test(kind)) {
      // A colour option has no recognisable shape, so it is identified by
      // elimination: the option that is not the size, frame or material one.
      // Without this the apparel colour step vanished on every translated
      // market (the option name no longer read "Color").
      var claimed = [
        this.detectOptionIndex('size'),
        this.detectOptionIndex('frame'),
        this.detectOptionIndex('material')
      ];
      for (var c = 0; c < names.length; c++) {
        if (claimed.indexOf(c) > -1) continue;
        var cvals = [];
        for (var q = 0; q < cfg.variants.length; q++) {
          var cv = cfg.variants[q].options[c];
          if (cv && cvals.indexOf(cv) === -1) cvals.push(cv);
        }
        // never mistake a size for a colour
        if (cvals.length && !cvals.some(function (v) { return /\d+\s*[x×]\s*\d+/.test(v); })) return c;
      }
      return -1;
    } else {
      return -1;
    }
    for (var i = 0; i < names.length; i++) {
      var vals = [];
      for (var k = 0; k < cfg.variants.length; k++) {
        var val = cfg.variants[k].options[i];
        if (val && vals.indexOf(val) === -1) vals.push(val);
      }
      if (vals.length && tests(vals)) return i;
    }
    return -1;
  };

  DBConfigurator.prototype.matchVariant = function () {
    var cfg = this.cfg;
    var state = this.state;
    var wants = [];
    var sizeIdx = this.optionIndex(cfg.sizeOptionName);
    var matIdx = this.optionIndex(cfg.materialOptionName);
    var colIdx = this.optionIndex(cfg.colorOptionName);
    var frameIdx = this.optionIndex(cfg.frameOptionName || 'Frame');
    if (sizeIdx > -1 && state.size) wants.push([sizeIdx, state.size]);
    if (matIdx > -1 && state.material) wants.push([matIdx, state.material]);
    if (colIdx > -1 && state.color) wants.push([colIdx, state.color]);
    // Frame is a real variant option on wall art: it must drive the variant
    // (and the price), never travel as a decorative line item property only.
    if (frameIdx > -1 && state.frame) wants.push([frameIdx, state.frame]);

    var best = null, bestScore = -1;
    cfg.variants.forEach(function (v) {
      var score = 0;
      for (var i = 0; i < wants.length; i++) {
        // Loose matching: "50x70 cm" (CSV/state) must hit "M | 50x70 cm"
        // (real option), "Black" must hit "Black Frame" — otherwise the cart
        // silently falls back to variants[0] and charges the wrong price.
        if (optionMatches(wants[i][1], v.options[wants[i][0]])) score++;
        else return;
      }
      if (v.available) score += 0.5;
      if (score > bestScore) { bestScore = score; best = v; }
    });
    return best || cfg.variants[0] || null;
  };

  DBConfigurator.prototype.priceForOption = function (stepId, label) {
    var mapped = { size: this.cfg.sizeOptionName, material: this.cfg.materialOptionName, color: this.cfg.colorOptionName, frame: this.cfg.frameOptionName || 'Frame' };
    if (!(stepId in mapped)) return null;
    if (this.optionIndex(mapped[stepId]) === -1) return null;
    var saved = this.state;
    this.state = Object.assign({}, saved);
    this.state[stepId] = label;
    var v = this.matchVariant();
    this.state = saved;
    return v ? v.price : null;
  };

  /* ---------------------------- overlay ----------------------------- */

  DBConfigurator.prototype.open = function () {
    var self = this;
    Promise.resolve(this.ready).then(function () {
      if (!self.overlay) self.mountOverlay();
      var ov = self.overlay;
      // While closing, the node lingers ~450ms for the fade-out. Bailing out on
      // isConnected made a re-open during that window a dead click: finish the
      // teardown immediately instead.
      if (ov.isConnected && ov.dataset.dbClosing === '1') {
        if (ov.parentNode) ov.parentNode.removeChild(ov);
        delete ov.dataset.dbClosing;
      }
      if (ov.isConnected) return;
      delete ov.dataset.dbClosing;
      document.body.appendChild(ov);
      // iOS-safe scroll lock: pin the body at the current scroll position.
      self._scrollY = window.scrollY || window.pageYOffset || 0;
      document.body.style.top = (-self._scrollY) + 'px';
      document.body.classList.add('db-ov-lock');
      requestAnimationFrame(function () { ov.classList.add('is-open'); });

      self.stepIndex = 0;
      self.renderAll();
      self.focusTrapStart();
      try { history.pushState({ dbCfg: 1 }, ''); } catch (e) { /* ignore */ }
      self._popstate = self.close.bind(self, true);
      window.addEventListener('popstate', self._popstate);
    });
  };

  DBConfigurator.prototype.close = function (fromHistory) {
    var ov = this.overlay;
    if (!ov || !ov.isConnected) return;
    this.finishFlip(true);
    window.removeEventListener('popstate', this._popstate);
    if (!fromHistory) { try { if (history.state && history.state.dbCfg) history.back(); } catch (e) { /* ignore */ } }
    ov.classList.remove('is-open');
    ov.dataset.dbClosing = '1';
    document.body.classList.remove('db-ov-lock');
    document.body.style.top = '';
    if (this._scrollY != null) { window.scrollTo(0, this._scrollY); this._scrollY = null; }
    setTimeout(function () {
      if (ov.dataset.dbClosing !== '1') return; // re-opened meanwhile
      if (ov.parentNode) ov.parentNode.removeChild(ov);
      delete ov.dataset.dbClosing;
    }, reducedMotion ? 0 : 450);
    this.saveState();
  };

  DBConfigurator.prototype.mountOverlay = function () {
    var tpl = this.querySelector('[data-db-overlay-template]');
    var frag = tpl.content.cloneNode(true);
    this.overlay = frag.querySelector('.db-ov');
    var ov = this.overlay;
    var self = this;
    var cfg = this.cfg;

    ov.setAttribute('data-db-theme', cfg.stageTheme === 'dark' ? 'dark' : 'light');
    ov.style.setProperty('--db-accent', cfg.accent || '#0075e3');
    if (cfg.sceneImage) {
      ov.querySelector('[data-db-scene]').style.backgroundImage =
        'linear-gradient(180deg, rgba(255,255,255,.72), rgba(255,255,255,.85)), url("' + cfg.sceneImage + '")';
    }

    ov.querySelector('[data-db-title]').textContent = cfg.productTitle;
    ov.querySelector('[data-db-label-close]').textContent = this.label('close');
    ov.querySelector('[data-db-add-label]').textContent = this.label('addToCart');
    ov.querySelector('[data-db-ar-hint]').textContent = this.label('arHint');
    var btn3dLabel = ov.querySelector('[data-db-label-3d]');
    if (btn3dLabel) btn3dLabel.textContent = this.label('viewer3d');

    ov.querySelector('[data-db-close]').addEventListener('click', function () { self.close(false); });
    ov.querySelector('[data-db-close-backdrop]').addEventListener('click', function () { self.close(false); });
    ov.querySelector('[data-db-add]').addEventListener('click', this.addToCart.bind(this));
    ov.querySelector('[data-db-prev]').addEventListener('click', function () { self.goto(self.stepIndex - 1); });
    ov.querySelector('[data-db-next]').addEventListener('click', function () { self.goto(self.stepIndex + 1); });

    ov.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        var v3d = ov.querySelector('[data-db-3d]');
        if (v3d && !v3d.hidden) self.close3d();
        else self.close(false);
      }
      if (e.key === 'Tab') self.cycleFocus(e);
    });

    var open3d = ov.querySelector('[data-db-3d-open]');
    open3d.addEventListener('click', this.open3d.bind(this));
    ov.querySelector('[data-db-3d-close]').addEventListener('click', this.close3d.bind(this));

    if (window.matchMedia('(pointer: fine)').matches && !reducedMotion) {
      var stage = ov.querySelector('[data-db-stage]');
      var art = ov.querySelector('[data-db-art]');
      var raf = null;
      stage.addEventListener('pointermove', function (e) {
        if (raf) return;
        raf = requestAnimationFrame(function () {
          raf = null;
          var r = stage.getBoundingClientRect();
          var px = (e.clientX - r.left) / r.width - 0.5;
          var py = (e.clientY - r.top) / r.height - 0.5;
          art.style.setProperty('--tilt-y', (px * 10).toFixed(2) + 'deg');
          art.style.setProperty('--tilt-x', (py * -8).toFixed(2) + 'deg');
        });
      });
      stage.addEventListener('pointerleave', function () {
        art.style.setProperty('--tilt-y', '0deg');
        art.style.setProperty('--tilt-x', '0deg');
      });
    }

    // apparel: preview ratio follows the product image
    var img = ov.querySelector('[data-db-img]');
    img.addEventListener('load', function () {
      if (self.modeNow() === 'apparel' && img.naturalWidth > 0) {
        var art2 = ov.querySelector('[data-db-art]');
        art2.style.setProperty('--art-ratio', img.naturalWidth + ' / ' + img.naturalHeight);
        art2.style.setProperty('--art-w', '46vmin');
      }
    });

    this.steps = this.buildSteps();
    this.buildTabs();
    this.buildPanes();
  };

  DBConfigurator.prototype.focusTrapStart = function () {
    var first = this.overlay.querySelector('[data-db-close]');
    if (first) first.focus();
  };

  DBConfigurator.prototype.cycleFocus = function (e) {
    var focusables = this.overlay.querySelectorAll('button, input, [tabindex]:not([tabindex="-1"])');
    var list = Array.prototype.filter.call(focusables, function (n) { return n.offsetParent !== null && !n.disabled; });
    if (!list.length) return;
    var first = list[0], last = list[list.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  /* ----------------------- tabs + panes build ----------------------- */

  DBConfigurator.prototype.buildTabs = function () {
    var self = this;
    var bar = this.overlay.querySelector('[data-db-tabs]');
    bar.innerHTML = '';
    this.steps.forEach(function (step, i) {
      var tab = el('button', 'db-tab');
      tab.type = 'button';
      tab.setAttribute('role', 'tab');
      tab.dataset.step = step.id;
      tab.appendChild(el('span', 'db-tab__dot'));
      tab.appendChild(document.createTextNode(self.label(step.id)));
      tab.addEventListener('click', function () { self.goto(i); });
      bar.appendChild(tab);
    });
  };

  DBConfigurator.prototype.buildPanes = function () {
    var self = this;
    var wrap = this.overlay.querySelector('[data-db-panes]');
    wrap.innerHTML = '';
    this.steps.forEach(function (step) {
      var pane = el('div', 'db-pane');
      pane.dataset.step = step.id;
      pane.appendChild(elText('h3', 'db-pane__title', self.label(step.id)));
      if (step.id === 'text') self.buildTextPane(pane);
      else if (step.id === 'design') self.buildDesignPane(pane);
      else if (step.id === 'upload') self.buildUploadPane(pane);
      else self.buildOptionsPane(pane, step);
      wrap.appendChild(pane);
    });
  };

  DBConfigurator.prototype.buildOptionsPane = function (pane, step) {
    var self = this;
    var grid = el('div', 'db-opts');
    grid.setAttribute('role', 'radiogroup');
    step.values.forEach(function (opt) {
      var card = el('button', 'db-opt');
      card.type = 'button';
      card.dataset.step = step.id;
      card.dataset.label = opt.label;
      card.setAttribute('role', 'radio');

      if (step.id === 'frame' || step.id === 'color') {
        if (opt.value === 'none') {
          card.appendChild(el('span', 'db-opt__swatch db-opt__swatch--none'));
        } else {
          var sw = el('span', 'db-opt__swatch');
          sw.style.setProperty('--swatch', safeColor(opt.value));
          card.appendChild(sw);
        }
      } else if (step.id === 'size' && self.modeNow() !== 'apparel') {
        var dims = String(opt.value).split('x');
        var rw = parseFloat(dims[0]) || 3, rh = parseFloat(dims[1]) || 4;
        var sz = el('span', 'db-opt__size');
        sz.style.aspectRatio = rw + ' / ' + rh;
        card.appendChild(sz);
      }
      card.appendChild(elText('span', null, opt.label));

      var price = self.priceForOption(step.id, opt.label);
      if (price != null) card.appendChild(elText('span', 'db-opt__price', formatMoney(price, self.cfg.currency)));

      card.addEventListener('click', function () {
        self.state[step.id] = opt.label;
        self.saveState();
        // A mapped material means a different Shopify product: reload it so the
        // sizes, availability and PRICES are the real ones for that material.
        if (step.id === 'material' && opt.handle) {
          self.switchMaterialProduct(opt.handle);
          return;
        }
        self.renderAll({ changed: step.id });
      });
      grid.appendChild(card);
    });
    pane.appendChild(grid);
  };

  DBConfigurator.prototype.buildDesignPane = function (pane) {
    var self = this;
    var preset = this.activePreset || { images: [], phrases: [] };

    if (preset.images && preset.images.length) {
      var grid = el('div', 'db-designs');
      preset.images.forEach(function (rawUrl, i) {
        var url = safeImageUrl(rawUrl);
        if (!url) return;
        var card = el('button', 'db-design');
        card.type = 'button';
        card.dataset.designImage = url;
        var im = document.createElement('img');
        im.src = url;
        im.alt = 'Design option ' + (i + 1);
        im.loading = 'lazy';
        card.appendChild(im);
        card.addEventListener('click', function () {
          self.state.designImage = url;
          self.state.designLabel = (preset.label || 'Design') + ' #' + (i + 1);
          self.saveState();
          self.renderAll({ changed: 'design' });
        });
        grid.appendChild(card);
      });
      pane.appendChild(grid);
    }

    if (preset.phrases && preset.phrases.length) {
      var list = el('div', 'db-phrases');
      preset.phrases.forEach(function (phrase) {
        var chip = elText('button', 'db-phrase', phrase);
        chip.type = 'button';
        chip.dataset.phrase = phrase;
        chip.addEventListener('click', function () {
          var L = self.activeLayerObj();
          if (!L) return;
          L.text = phrase.slice(0, L.maxChars);
          L.changed = true;
          L.enabled = true;
          self.saveState();
          self.renderAll({ changed: 'text' });
        });
        list.appendChild(chip);
      });
      pane.appendChild(list);
    }
  };

  DBConfigurator.prototype.buildUploadPane = function (pane) {
    var self = this;
    var note = elText('p', 'db-pane__note', this.label('uploadNote'));
    var zone = el('label', 'db-upload');
    // Keyboard-operable: the label is focusable and Enter/Space opens the picker.
    zone.tabIndex = 0;
    zone.setAttribute('role', 'button');
    zone.innerHTML =
      '<svg viewBox="0 0 24 24" width="26" height="26" fill="none" aria-hidden="true"><path d="M12 16V4m0 0 4 4m-4-4L8 8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
    zone.appendChild(elText('span', null, this.label('uploadCta'))).setAttribute('data-db-upload-label', '');
    var input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/webp';
    zone.appendChild(input);
    zone.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); input.click(); }
    });
    input.addEventListener('change', function () {
      var file = input.files && input.files[0];
      if (!file) return;
      if (file.size > 8 * 1024 * 1024) {
        zone.querySelector('[data-db-upload-label]').textContent = 'Max 8 MB — please choose a smaller file.';
        return;
      }
      var reader = new FileReader();
      reader.onload = function () {
        self.state.uploadDataUrl = String(reader.result);
        self.state.uploadName = file.name;
        zone.classList.add('has-file');
        zone.querySelector('[data-db-upload-label]').textContent = file.name + ' ✓';
        self.saveState();
        self.renderAll({ changed: 'upload' });
      };
      reader.readAsDataURL(file);
    });
    pane.appendChild(note);
    pane.appendChild(zone);
  };

  /* One input row per personalized layer. Shared by the overlay text pane and
     the in-page (inline) editor. ctx: { compact: bool, onChange: fn } */
  DBConfigurator.prototype.buildLayerFields = function (wrap, ctx) {
    var self = this;
    ctx = ctx || {};
    wrap.innerHTML = '';

    (this.state.layers || []).forEach(function (L, idx) {
      if (!L.allow) return; // merchant locked this element (Allow personalization = OFF)
      var row = el('div', 'db-layer');
      row.dataset.layerKey = L.key;
      if (!L.enabled) row.classList.add('is-off');

      var head = el('div', 'db-layer__head');
      head.appendChild(elText('label', 'db-layer__label', L.label));
      var count = elText('span', 'db-layer__count', L.text.length + ' / ' + L.maxChars);
      head.appendChild(count);

      if (L.removable || (self.pz.allowRemoveLayers && !ctx.compact)) {
        var rm = elText('button', 'db-layer__remove', L.enabled ? '✕' : '↺');
        rm.type = 'button';
        rm.title = self.label('removeLayer');
        rm.setAttribute('aria-label', self.label('removeLayer') + ' ' + L.label);
        rm.addEventListener('click', function () {
          if (L.removable && L.enabled) {
            var i = self.state.layers.indexOf(L);
            if (i > -1) self.state.layers.splice(i, 1);
            if (self.state.activeLayer >= self.state.layers.length) self.state.activeLayer = 0;
          } else {
            L.enabled = !L.enabled; // merchant layers toggle off/on instead of vanishing
          }
          self.saveState();
          self.rebuildTextUIs();
          self.renderAll({ changed: 'text' });
        });
        head.appendChild(rm);
      }
      row.appendChild(head);

      var input = el('input', 'db-textfield');
      input.type = 'text';
      input.maxLength = L.maxChars;
      input.placeholder = L.defaultText || self.label('textPlaceholder');
      input.value = L.text;
      input.disabled = !L.enabled;
      input.setAttribute('aria-label', L.label);
      input.dataset.dbLayerInput = L.key;
      input.addEventListener('focus', function () {
        self.state.activeLayer = self.state.layers.indexOf(L);
        if (!ctx.compact) self.renderAll({ changed: 'text', quiet: true });
      });
      input.addEventListener('input', function () {
        L.text = input.value;
        L.changed = true;
        count.textContent = input.value.length + ' / ' + L.maxChars;
        self.saveState();
        if (ctx.onChange) ctx.onChange();
        else self.applyPreview({ changed: 'text', quiet: true });
      });
      row.appendChild(input);
      wrap.appendChild(row);
    });

    // Customer settings — "Add layers": extra text layers on demand.
    if (this.pz.allowAddLayers) {
      var add = elText('button', 'db-chip db-chip--add', this.label('addLayer'));
      add.type = 'button';
      add.addEventListener('click', function () {
        var n = self.state.layers.length + 1;
        self.state.layers.push({
          key: 'custom-' + Date.now(), label: 'Text ' + n, text: '', defaultText: '',
          color: TEXT_COLORS[0], font: 'default', pos: 'center', sizeMul: 1,
          allow: true, sample: false, maxChars: 40, enabled: true,
          changed: false, removable: true
        });
        self.state.activeLayer = self.state.layers.length - 1;
        self.saveState();
        self.rebuildTextUIs();
        self.renderAll({ changed: 'text', quiet: true });
      });
      wrap.appendChild(add);
    }
  };

  /* Rebuild every mounted layer-fields container (overlay pane + inline). */
  DBConfigurator.prototype.rebuildTextUIs = function () {
    var self = this;
    if (this.overlay) {
      var wrap = this.overlay.querySelector('[data-db-layers]');
      if (wrap) this.buildLayerFields(wrap, {});
    }
    var inline = this.querySelector('[data-db-inline-layers]');
    if (inline) this.buildLayerFields(inline, { compact: true, onChange: this._inlineRefresh || null });
  };

  DBConfigurator.prototype.buildTextPane = function (pane) {
    var self = this;

    var wrap = el('div', 'db-layers');
    wrap.setAttribute('data-db-layers', '');
    this.buildLayerFields(wrap, {});
    pane.appendChild(wrap);

    var controls = el('div', 'db-text-controls');

    var fonts = el('div', 'db-chiplist');
    fonts.setAttribute('aria-label', 'Font');
    TEXT_FONTS.forEach(function (f) {
      var chip = el('button', 'db-chip', f.label);
      chip.type = 'button';
      chip.dataset.textFont = f.id;
      chip.addEventListener('click', function () {
        var L = self.activeLayerObj();
        if (!L) return;
        L.font = f.id;
        self.saveState();
        self.renderAll({ changed: 'text' });
      });
      fonts.appendChild(chip);
    });

    var colors = el('div', 'db-chiplist');
    colors.setAttribute('aria-label', 'Text color');
    TEXT_COLORS.forEach(function (c) {
      var chip = el('button', 'db-colorchip');
      chip.type = 'button';
      chip.style.setProperty('--swatch', c);
      chip.dataset.textColor = c;
      chip.setAttribute('aria-label', 'Text color ' + c);
      chip.addEventListener('click', function () {
        var L = self.activeLayerObj();
        if (!L) return;
        L.color = c;
        self.saveState();
        self.renderAll({ changed: 'text' });
      });
      colors.appendChild(chip);
    });

    var sizes = el('div', 'db-chiplist');
    sizes.setAttribute('aria-label', 'Text size');
    TEXT_SIZES.forEach(function (s) {
      var chip = el('button', 'db-chip', s.label);
      chip.type = 'button';
      chip.dataset.textSize = String(s.id);
      chip.addEventListener('click', function () {
        var L = self.activeLayerObj();
        if (!L) return;
        L.sizeMul = s.id;
        self.saveState();
        self.renderAll({ changed: 'text' });
      });
      sizes.appendChild(chip);
    });

    controls.appendChild(fonts);
    controls.appendChild(colors);
    controls.appendChild(sizes);

    // Customer settings — "Choose layouts": position chips only when allowed.
    if (this.pz.allowLayouts !== false) {
      var positions = el('div', 'db-chiplist');
      positions.setAttribute('aria-label', 'Text position');
      [['bottom', 'Bottom'], ['center', 'Center'], ['top', 'Top']].forEach(function (p) {
        var chip = el('button', 'db-chip', p[1]);
        chip.type = 'button';
        chip.dataset.textPos = p[0];
        chip.addEventListener('click', function () {
          var L = self.activeLayerObj();
          if (!L) return;
          L.pos = p[0];
          self.saveState();
          self.renderAll({ changed: 'text' });
        });
        positions.appendChild(chip);
      });
      controls.appendChild(positions);
    }

    // Customer settings — "Change background color".
    if (this.pz.allowBgColor) {
      var bgWrap = el('div', 'db-chiplist db-chiplist--bg');
      bgWrap.setAttribute('aria-label', this.label('background'));
      bgWrap.appendChild(elText('span', 'db-chiplist__title', this.label('background')));
      BG_COLORS.forEach(function (c) {
        var chip = el('button', 'db-colorchip');
        chip.type = 'button';
        if (c) chip.style.setProperty('--swatch', c);
        else chip.classList.add('db-colorchip--none');
        chip.dataset.bgColor = c;
        chip.setAttribute('aria-label', c ? 'Background ' + c : 'No background');
        chip.addEventListener('click', function () {
          self.state.bgColor = c;
          self.saveState();
          self.renderAll({ changed: 'finish' });
        });
        bgWrap.appendChild(chip);
      });
      controls.appendChild(bgWrap);
    }

    pane.appendChild(controls);
  };

  /* ---------------------------- render ------------------------------ */

  DBConfigurator.prototype.goto = function (index) {
    if (index < 0 || index >= this.steps.length) return;
    this.stepIndex = index;
    this.renderAll();
  };

  DBConfigurator.prototype.renderAll = function (info) {
    info = info || {};
    var ov = this.overlay;
    var self = this;
    var current = this.steps[this.stepIndex];

    ov.querySelectorAll('.db-tab').forEach(function (tab, i) {
      tab.classList.toggle('is-active', i === self.stepIndex);
      tab.setAttribute('aria-selected', i === self.stepIndex ? 'true' : 'false');
      var id = tab.dataset.step;
      var done = id === 'text' ? (self.state.layers || []).some(function (L) { return L.enabled && L.text; })
        : id === 'design' ? !!self.state.designImage
          : id === 'upload' ? !!self.state.uploadName
            : !!self.state[id];
      tab.classList.toggle('is-done', done);
    });

    ov.querySelectorAll('.db-pane').forEach(function (pane) {
      pane.classList.toggle('is-active', pane.dataset.step === current.id);
    });

    ov.querySelectorAll('.db-opt').forEach(function (card) {
      var sel = self.state[card.dataset.step] === card.dataset.label;
      card.classList.toggle('is-selected', sel);
      card.setAttribute('aria-checked', sel ? 'true' : 'false');
    });
    ov.querySelectorAll('[data-design-image]').forEach(function (card) {
      card.classList.toggle('is-selected', card.dataset.designImage === self.state.designImage);
    });
    var active = this.activeLayerObj();
    ov.querySelectorAll('[data-phrase]').forEach(function (chip) {
      chip.classList.toggle('is-selected', !!active && chip.dataset.phrase === active.text);
    });
    ov.querySelectorAll('[data-text-font]').forEach(function (chip) {
      chip.classList.toggle('is-selected', !!active && chip.dataset.textFont === active.font);
    });
    ov.querySelectorAll('[data-text-color]').forEach(function (chip) {
      chip.classList.toggle('is-selected', !!active && chip.dataset.textColor === active.color);
    });
    ov.querySelectorAll('[data-text-pos]').forEach(function (chip) {
      chip.classList.toggle('is-selected', !!active && chip.dataset.textPos === active.pos);
    });
    ov.querySelectorAll('[data-text-size]').forEach(function (chip) {
      chip.classList.toggle('is-selected', !!active && parseFloat(chip.dataset.textSize) === active.sizeMul);
    });
    ov.querySelectorAll('[data-bg-color]').forEach(function (chip) {
      chip.classList.toggle('is-selected', chip.dataset.bgColor === (self.state.bgColor || ''));
    });
    ov.querySelectorAll('[data-db-layer-input]').forEach(function (input) {
      var key = input.dataset.dbLayerInput;
      var L = (self.state.layers || []).find(function (x) { return x.key === key; });
      if (L && input.value !== L.text && document.activeElement !== input) input.value = L.text;
    });
    ov.querySelectorAll('.db-layer').forEach(function (row) {
      var L = (self.state.layers || []).find(function (x) { return x.key === row.dataset.layerKey; });
      row.classList.toggle('is-active', !!L && L === active);
      row.classList.toggle('is-off', !!L && !L.enabled);
    });

    ov.querySelector('[data-db-prev]').disabled = this.stepIndex === 0;
    ov.querySelector('[data-db-next]').disabled = this.stepIndex === this.steps.length - 1;
    ov.querySelector('[data-db-progress-fill]').style.width =
      (((this.stepIndex + 1) / this.steps.length) * 100).toFixed(1) + '%';

    var open3d = ov.querySelector('[data-db-3d-open]');
    open3d.hidden = !(this.cfg.steps.viewer3d.enabled && this.modeNow() !== 'apparel' && window.DBGlb);

    this.applyCamera(current.id);
    if (info.changed && !info.quiet && FLIP_STEPS.indexOf(info.changed) > -1) {
      this.flipPreview(info);
    } else {
      this.finishFlip(true); // click su altro step a metà flip: chiudi prima il flip
      this.applyPreview(info);
    }
    this.applyPrice(info);
  };

  DBConfigurator.prototype.applyCamera = function (stepId) {
    var cam = this.overlay.querySelector('[data-db-cam]');
    var preset = CAMERA_PRESETS[stepId] || CAMERA_PRESETS.size;
    var y = preset.y;
    var activeL = this.activeLayerObj();
    if (stepId === 'text' && activeL && activeL.pos === 'center') y = 0;
    if (stepId === 'text' && activeL && activeL.pos === 'top') y = 14;
    cam.style.setProperty('--cam-x', preset.x + '%');
    cam.style.setProperty('--cam-y', y + '%');
    cam.style.setProperty('--cam-scale', preset.s);
  };

  DBConfigurator.prototype.currentFrameHex = function () {
    // Real product option values win (they are what the variant is keyed on).
    var realFrames = this.optionValues(this.cfg.frameOptionName || 'Frame');
    if (realFrames.length) {
      for (var r = 0; r < realFrames.length; r++) {
        if (realFrames[r] === this.state.frame) return frameHexFromLabel(realFrames[r]);
      }
    }
    var frames = csvPairs(this.cfg.steps.frame.values, 'none');
    for (var i = 0; i < frames.length; i++) if (frames[i].label === this.state.frame) return frames[i].value;
    return 'none';
  };

  DBConfigurator.prototype.currentBorderPct = function () {
    var borders = csvPairs(this.cfg.steps.border.values, '0');
    for (var i = 0; i < borders.length; i++) if (borders[i].label === this.state.border) return parseFloat(borders[i].value) || 0;
    return 0;
  };

  DBConfigurator.prototype.currentSizeCm = function () {
    // Always resolve to REAL centimetres — an inches market ("L | 30x40 in")
    // must still print at 75x100 cm, not at a 30x40 file.
    var direct = sizeCmKey(this.state.size);
    if (/^\d/.test(direct) && direct.indexOf('x') > 0) {
      var dp = direct.split('x');
      return { w: parseFloat(dp[0]) || 50, h: parseFloat(dp[1]) || 70 };
    }
    var sizes = csvPairs(this.cfg.steps.size.values);
    for (var i = 0; i < sizes.length; i++) {
      if (sizes[i].label === this.state.size) {
        var parts = String(sizes[i].value).split('x');
        return { w: parseFloat(parts[0]) || 50, h: parseFloat(parts[1]) || 70 };
      }
    }
    return { w: 50, h: 70 };
  };

  DBConfigurator.prototype.previewImage = function () {
    if (this.state.uploadDataUrl) return this.state.uploadDataUrl;
    if (this.state.designImage) return this.state.designImage;   // preset design
    if (this.cfg.designImage) return this.cfg.designImage;       // naked design metafield (base)
    var v = this.matchVariant();
    return (v && v.image) || this.cfg.featuredImage;
  };

  DBConfigurator.prototype.finishFlip = function (applyNow) {
    if (this._flipWatchdog) { clearTimeout(this._flipWatchdog); this._flipWatchdog = null; }
    var art = this.overlay && this.overlay.querySelector('[data-db-art]');
    if (art) art.classList.remove('is-flip-out', 'is-flip-in');
    if (this._flipPending) {
      this._flipPending = false;
      if (applyNow) this.applyPreview({ quiet: true });
    }
  };

  DBConfigurator.prototype.flipPreview = function (info) {
    var self = this;
    var art = this.overlay.querySelector('[data-db-art]');
    if (!art || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.applyPreview(info);
      return;
    }

    if (!this._flipBound) {
      this._flipBound = true;
      var onFlipEvent = function (e) {
        if (e.target !== art) return;
        if (e.type === 'animationend' && e.animationName === 'db-flip-out') {
          // 90°, di taglio: applica la nuova cornice mentre è invisibile
          self._flipPending = false;
          self.applyPreview({ quiet: true });
          art.classList.remove('is-flip-out');
          art.classList.add('is-flip-in');
        } else if (e.type === 'animationend' && e.animationName === 'db-flip-in') {
          self.finishFlip(false);
        } else if (e.type === 'animationcancel' &&
                   !art.classList.contains('is-flip-out') &&
                   !art.classList.contains('is-flip-in')) {
          self.finishFlip(true);
        }
      };
      art.addEventListener('animationend', onFlipEvent);
      art.addEventListener('animationcancel', onFlipEvent);
    }

    this.finishFlip(true); // mai due flip sovrapposti
    this._flipPending = true;
    art.classList.remove('is-settle');
    void art.offsetWidth;
    art.classList.add('is-flip-out');
    // watchdog: le classi non restano mai appese se un evento va perso
    this._flipWatchdog = setTimeout(function () { self.finishFlip(true); }, FLIP_HALF_MS * 2 + 120);
  };

  /* 3/4 pose: -35deg quando c'è una cornice, frontale altrimenti.
     Ritorna true se la posa è cambiata in questo render. */
  DBConfigurator.prototype.applyPose = function (on) {
    var cam = this.overlay.querySelector('[data-db-cam]');
    if (!cam) return false;
    var next = on ? '1' : '0';
    if (cam.style.getPropertyValue('--pose-on') === next) return false;
    // la rotazione È la transizione: un settle in corso la sovrascriverebbe
    var art = this.overlay.querySelector('[data-db-art]');
    if (art) art.classList.remove('is-settle');
    cam.style.setProperty('--pose-ry', on ? '-35deg' : '0deg');
    cam.style.setProperty('--pose-rx', on ? '2deg' : '0deg');
    cam.style.setProperty('--pose-s', on ? 0.95 : 1);
    cam.style.setProperty('--pose-on', next);
    return true;
  };

  DBConfigurator.prototype.applyPreview = function (info) {
    info = info || {};
    var ov = this.overlay;
    var state = this.state;
    var apparel = this.modeNow() === 'apparel';
    var art = ov.querySelector('[data-db-art]');
    var img = ov.querySelector('[data-db-img]');
    var dims = ov.querySelector('[data-db-dims]');

    var src = this.previewImage();
    if (src && img.getAttribute('src') !== src) {
      img.classList.add('is-swap');
      var done = function () {
        img.classList.remove('is-swap');
        img.removeEventListener('load', done);
        img.removeEventListener('error', done);
      };
      img.addEventListener('load', done);
      img.addEventListener('error', done); // never leave the artwork blank
      img.src = src;
      if (src.indexOf('data:') !== 0) {
        ov.querySelector('[data-db-reflection]').style.setProperty('--reflect-img', 'url("' + src + '")');
      }
    }

    if (!apparel) {
      var cm = this.currentSizeCm();
      art.style.setProperty('--art-ratio', cm.w + ' / ' + cm.h);
      art.style.setProperty('--art-w', (22 + cm.w * 0.22).toFixed(1) + 'vmin');
      dims.textContent = state.size || '';

      var frameHex = this.currentFrameHex();
      var hasFrame = frameHex && frameHex !== 'none';
      // larghezza sempre dimensionata: il crossfade non deve mai "crescere"
      art.style.setProperty('--frame-w', 'calc(' + (22 + cm.w * 0.22).toFixed(1) + 'vmin * 0.035)');
      if (hasFrame) art.style.setProperty('--frame-color', frameHex); // ultimo colore tenuto per il fade-out
      art.style.setProperty('--frame-on', hasFrame ? 1 : 0);
      var poseChanged = this.applyPose(hasFrame);

      var pct = this.currentBorderPct();
      art.style.setProperty('--mat-w', pct + '%');
      art.style.setProperty('--mat-on', pct > 0 ? 1 : 0);

      var glossy = /gloss|lucid/i.test(state.finish || '');
      art.style.setProperty('--gloss', glossy ? 0.55 : 0.12);
    } else {
      dims.textContent = [state.color, state.size].filter(Boolean).join(' · ');
      art.style.setProperty('--frame-w', '0px');
      art.style.setProperty('--mat-w', '0%');
      art.style.setProperty('--mat-on', 0);
      art.style.setProperty('--gloss', 0);
      art.style.setProperty('--frame-on', 0);
      var poseChanged = this.applyPose(false);
    }

    // Customer settings — "Change background color" (mat / border area).
    var mat = ov.querySelector('[data-db-mat]');
    if (mat) mat.style.background = state.bgColor ? safeColor(state.bgColor) : '';

    this.renderTextLayers(ov.querySelector('[data-db-text]'), apparel);

    if (info.changed && !poseChanged && !info.quiet && !reducedMotion) {
      art.classList.remove('is-settle');
      void art.offsetWidth;
      art.classList.add('is-settle');
      if (info.changed === 'finish') {
        var sweep = ov.querySelector('[data-db-sweep]');
        sweep.classList.remove('is-on');
        void sweep.offsetWidth;
        sweep.classList.add('is-on');
      }
    }
  };

  /* Live preview of ALL text layers (one absolutely-positioned line each). */
  DBConfigurator.prototype.renderTextLayers = function (container, apparel) {
    if (!container) return;
    container.classList.add('is-multi');
    container.textContent = '';
    var stacks = { top: 0, center: 0, bottom: 0 };
    (this.state.layers || []).forEach(function (L) {
      if (!L.enabled || !L.text) return;
      var pos = apparel ? 'center' : L.pos;
      var line = elText('span', 'db-art__textline', L.text);
      line.dataset.pos = pos;
      line.dataset.font = L.font;
      line.dataset.stack = String(Math.min(stacks[pos] || 0, 2));
      stacks[pos] = (stacks[pos] || 0) + 1;
      line.style.setProperty('--line-color', safeColor(L.color));
      line.style.setProperty('--line-scale', String(L.sizeMul || 1));
      container.appendChild(line);
    });
  };

  DBConfigurator.prototype.applyPrice = function (info) {
    var v = this.matchVariant();
    var priceEl = this.overlay.querySelector('[data-db-price]');
    if (!v) { priceEl.textContent = ''; return; }
    var text = formatMoney(v.price, this.cfg.currency);
    if (priceEl.textContent !== text) {
      priceEl.textContent = text;
      if (info && info.changed && !reducedMotion) {
        priceEl.classList.remove('is-bump');
        void priceEl.offsetWidth;
        priceEl.classList.add('is-bump');
      }
    }
  };

  /* --------------------------- add to cart -------------------------- */

  DBConfigurator.prototype.lineItemProperties = function (variant, cz) {
    var s = this.state;
    var props = {};
    if (s.size) props['Size'] = s.size;
    if (s.color) props['Color'] = s.color;
    if (s.frame && !/^no/i.test(s.frame)) props['Frame'] = s.frame;
    if (s.material) props['Material'] = s.material;
    if (s.finish) props['Finish'] = s.finish;
    // Solo per le tele: e' un prodotto diverso lato fornitore (SKU e costo).
    if (s.thickness && this.isCanvasSelected()) props['Thickness'] = s.thickness;
    if (s.border && !/^no/i.test(s.border)) props['Border'] = s.border;
    if (s.placement) props['Print placement'] = s.placement;
    if (s.bgColor) props['Background'] = s.bgColor;
    if (s.designImage) props['_Design image'] = s.designImage;
    if (s.designLabel) props['Design'] = s.designLabel;
    if (s.uploadName) props['Customer image'] = s.uploadName + ' (file to be collected from customer)';
    this.printedLayers(false).forEach(function (L) {
      props[L.label] = L.text + (L.sample && !L.changed ? ' (sample — not printed)' : '');
    });
    if (this.activePreset) props['_Goal'] = this.activePreset.id;
    // Gelato Personalization Studio parity: these are the properties their
    // Shopify app writes; our worker reads _customizationId at order time and
    // sends the personalized print file to Gelato production.
    if (cz && cz.id) {
      props['_customizationId'] = cz.id;
      props['_inPagePersonalization'] = 'true';
      props['_productPreviewUrl'] = cz.previewUrl;
      props['_printFileUrl'] = cz.printUrl;
      props['_variantId'] = String(variant.id);
    }
    return props;
  };

  /* True when the composition differs from the stock design (something must be
     produced with a custom print file). */
  DBConfigurator.prototype.hasPersonalization = function () {
    if (this.printedLayers(true).length) return true;
    if (this.state.uploadDataUrl) return true;
    if (this.state.bgColor) return true;
    if (this.currentBorderPct() > 0) return true;
    return false;
  };

  /* Cheap cached reachability check so a dead worker never blocks checkout. */
  DBConfigurator.prototype.pingWorker = function () {
    var self = this;
    if (!this.workerUrl || this.pz.productionSync === false) return;
    var cacheKey = 'db-worker-ok:' + this.workerUrl;
    try {
      var cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
      if (cached && Date.now() - cached.at < 600000) { self._workerDown = !cached.ok; return; }
    } catch (e) { /* ignore */ }
    var ctl = ('AbortController' in window) ? new AbortController() : null;
    var timer = setTimeout(function () { if (ctl) ctl.abort(); }, 5000);
    fetch(this.workerUrl + '/health', { signal: ctl ? ctl.signal : undefined })
      .then(function (r) { return r.ok; })
      .catch(function () { return false; })
      .then(function (ok) {
        clearTimeout(timer);
        self._workerDown = !ok;
        try { sessionStorage.setItem(cacheKey, JSON.stringify({ ok: ok, at: Date.now() })); } catch (e) { /* ignore */ }
      });
  };

  /* Save the personalization on the worker (like Gelato's customization):
     1) register state -> { id, previewUrl, printUrl }
     2) PUT the cart-thumbnail mockup and the hi-res print file (raw bytes).
     Resolves null on ANY failure — checkout must never be blocked. */
  DBConfigurator.prototype.saveCustomization = function (variant) {
    var self = this;
    if (!this.workerUrl || this.pz.productionSync === false) return Promise.resolve(null);
    if (this._workerDown) return Promise.resolve(null);
    if (!this.hasPersonalization()) return Promise.resolve(null);

    function withTimeout(p, ms) {
      return Promise.race([p, new Promise(function (_, rej) {
        setTimeout(function () { rej(new Error('timeout')); }, ms);
      })]);
    }

    var cm = this.currentSizeCm();
    var meta = {
      productId: this.cfg.productId,
      variantId: variant.id,
      productTitle: this.cfg.productTitle,
      variantTitle: variant.title,
      state: {
        size: this.state.size, frame: this.state.frame, material: this.state.material,
        finish: this.state.finish, thickness: this.state.thickness,
        border: this.state.border, bgColor: this.state.bgColor,
        widthCm: cm.w, heightCm: cm.h,
        layers: this.printedLayers(false).map(function (L) {
          return { label: L.label, text: L.text, color: L.color, font: L.font, pos: L.pos, sizeMul: L.sizeMul, printed: !(L.sample && !L.changed) };
        }),
        designImage: this.cfg.designImageFull || this.state.designImage || this.cfg.designImage || '',
        goal: this.activePreset ? this.activePreset.id : null
      }
    };

    var created;
    return withTimeout(
      fetch(this.workerUrl + '/customization-create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(meta)
      }),
      10000
    )
      .then(function (r) {
        if (!r.ok) throw new Error('create ' + r.status);
        return r.json();
      })
      .then(function (j) {
        if (!j || !j.id) throw new Error('no id');
        created = j;
        return Promise.all([
          self.composeBlob({ raw: false, px: 900, quality: 0.85 }),          // cart thumbnail mockup
          self.composeBlob({ raw: true, forPrint: true, quality: 0.95 }),    // print file (border included)
          // certificate artwork: design + text ONLY (no border/frame — the
          // border belongs on the printed product, not on the certificate)
          self.composeBlob({ raw: true, noBorder: true, px: 700, quality: 0.85 }).catch(function () { return null; })
        ]);
      })
      .then(function (blobs) {
        var puts = [
          fetch(created.previewUrl, { method: 'PUT', headers: { 'Content-Type': 'image/jpeg' }, body: blobs[0] }),
          fetch(created.printUrl, { method: 'PUT', headers: { 'Content-Type': 'image/jpeg' }, body: blobs[1] })
        ];
        if (blobs[2] && created.certUrl) {
          puts.push(fetch(created.certUrl, { method: 'PUT', headers: { 'Content-Type': 'image/jpeg' }, body: blobs[2] })
            .catch(function () { return { ok: true }; })); // cert asset is best-effort
        }
        return withTimeout(Promise.all(puts), 45000);
      })
      .then(function (rs) {
        if (!rs[0].ok || !rs[1].ok) throw new Error('upload failed');
        return created;
      })
      .catch(function (err) {
        console.warn('[db-configurator] customization save skipped:', err && err.message);
        return null;
      });
  };

  DBConfigurator.prototype.addToCart = function (evt) {
    var self = this;
    var btn = (evt && evt.currentTarget && evt.currentTarget.matches && evt.currentTarget.matches('button'))
      ? evt.currentTarget
      : (this.overlay && this.overlay.querySelector('[data-db-add]'));
    if (!btn) return;
    var label = btn.querySelector('[data-db-add-label]') || btn;
    var variant = this.matchVariant();
    if (!variant) return;

    btn.disabled = true;
    label.textContent = this.hasPersonalization() && this.workerUrl && !this._workerDown
      ? this.label('saving')
      : this.label('adding');

    this.saveCustomization(variant)
      .then(function (cz) {
        label.textContent = self.label('adding');
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            items: [{ id: variant.id, quantity: 1, properties: self.lineItemProperties(variant, cz) }]
          })
        });
      })
      .then(function (res) {
        if (!res.ok) throw new Error('cart_error_' + res.status);
        return res.json();
      })
      .then(function () {
        label.textContent = self.label('added');
        btn.classList.add('is-added');
        setTimeout(function () {
          if (self.overlay && self.overlay.isConnected) self.close(false);
          window.location.href = '/cart';
        }, 700);
      })
      .catch(function (err) {
        console.error('[db-configurator] add to cart failed', err);
        btn.disabled = false;
        label.textContent = self.label('addToCart');
        btn.style.background = '#e63946';
        setTimeout(function () { btn.style.background = ''; }, 1200);
      });
  };

  /* ------------------- in-page personalization (Gelato) ------------------- */

  /* "Shopify in-page personalization": fields + live preview + add-to-cart
     rendered directly on the product page. The full editor stays reachable. */
  DBConfigurator.prototype.mountInline = function () {
    var self = this;
    var host = this.querySelector('[data-db-inline]');
    if (!host || host.dataset.mounted) return;
    if (!(this.state.layers || []).some(function (L) { return L.allow; }) && !this.pz.allowAddLayers) return;
    host.dataset.mounted = '1';
    host.hidden = false;

    host.appendChild(elText('h3', 'db-inline__title', this.label('inlineTitle')));

    var body = el('div', 'db-inline__body');

    var figure = el('div', 'db-inline__preview');
    var img = document.createElement('img');
    img.alt = 'Live preview of your personalized product';
    img.loading = 'lazy';
    figure.appendChild(img);
    body.appendChild(figure);

    var fields = el('div', 'db-inline__fields');
    var layersWrap = el('div', 'db-layers');
    layersWrap.setAttribute('data-db-inline-layers', '');
    fields.appendChild(layersWrap);

    var cta = el('button', 'db-cfg-open db-inline__add');
    cta.type = 'button';
    var ctaLabel = elText('span', null, this.label('addToCart'));
    ctaLabel.setAttribute('data-db-add-label', '');
    cta.appendChild(ctaLabel);
    cta.addEventListener('click', this.addToCart.bind(this));
    fields.appendChild(cta);

    var editorBtn = elText('button', 'db-inline__editor-link', this.label('openEditor'));
    editorBtn.type = 'button';
    editorBtn.addEventListener('click', function () { self.open(); });
    fields.appendChild(editorBtn);

    body.appendChild(fields);
    host.appendChild(body);

    var pending = null;
    var refresh = function () {
      if (pending) return;
      pending = setTimeout(function () {
        pending = null;
        self.composeCanvas({ raw: false, px: 640 }).then(function (canvas) {
          img.src = canvas.toDataURL('image/jpeg', 0.85);
        }).catch(function () { img.src = self.previewImage(); });
      }, 180);
    };
    this._inlineRefresh = refresh;
    this.buildLayerFields(layersWrap, { compact: true, onChange: refresh });
    refresh();
  };

  /* ----------------------------- 3D / AR ---------------------------- */

  DBConfigurator.prototype.open3d = function () {
    var self = this;
    var ov = this.overlay;
    var panel = ov.querySelector('[data-db-3d]');
    var mount = ov.querySelector('[data-db-3d-mount]');
    if (!window.DBGlb) return;
    panel.hidden = false;
    mount.innerHTML = '<div class="db-3d__loading">Building your 3D model…</div>';

    // Two paths:
    //  A) Hosted model (custom.model_3d_glb / native GLB media) → NATIVE AR
    //     (Scene Viewer on Android, Quick Look on iPhone). Reliable, no WebXR
    //     permission prompt. Used whenever the merchant/worker provides it.
    //  B) No hosted model → generate the framed artwork on the fly from the
    //     design image. Great for in-browser 3D; AR uses WebXR (best effort).
    // When a FRAME is selected we must generate the model on the fly so the
    // floating frame is visible (the hosted base model is frame-less). Without
    // a frame we use the hosted model → reliable NATIVE AR.
    var frameHexSel = this.currentFrameHex();
    var frameSelected = frameHexSel && frameHexSel !== 'none';
    var hosted = (!frameSelected) ? this.cfg.modelGlb : null;
    var buildStep = hosted
      ? DBGlb.loadModelViewer().then(function () { return { hostedSrc: hosted }; })
      : DBGlb.loadModelViewer()
        .then(function () { return self.composeTexture({ raw: true }); })
        .then(function (pngBytes) {
          var cm = self.currentSizeCm();
          var frameHex = self.currentFrameHex();
          var glossy = /gloss|lucid/i.test(self.state.finish || '');
          var isCanvas = /canvas|tela/i.test(self.state.material || '');
          var glb = DBGlb.buildPosterGlb({
            pngBytes: pngBytes,
            mime: 'image/png',
            width: cm.w / 100,
            height: cm.h / 100,
            depth: isCanvas ? 0.035 : 0.004,
            mode: isCanvas ? 'canvas' : 'poster',
            woodBytes: (isCanvas && window.DB_WOOD_BYTES) ? window.DB_WOOD_BYTES : null,
            frameColor: frameSelected ? frameHex : null,
            edgeColor: [0.9, 0.9, 0.88],
            roughness: glossy ? 0.25 : 0.9
          });
          if (self._glbUrl) URL.revokeObjectURL(self._glbUrl);
          self._glbUrl = URL.createObjectURL(new Blob([glb], { type: 'model/gltf-binary' }));
          return { blobSrc: self._glbUrl };
        });

    buildStep
      .then(function (model) {
        var mv = document.createElement('model-viewer');
        mv.setAttribute('src', model.hostedSrc || model.blobSrc);
        mv.setAttribute('alt', 'Your customized ' + self.cfg.productTitle + ' in 3D');
        mv.setAttribute('camera-controls', '');
        mv.setAttribute('environment-image', 'neutral');
        var wantRotate = (self.cfg.autoRotate3d !== false) && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (wantRotate) {
          mv.setAttribute('auto-rotate', '');
          mv.setAttribute('auto-rotate-delay', '1200');
          mv.setAttribute('rotation-per-second', '14deg');
        }
        mv.setAttribute('shadow-intensity', '1.1');
        mv.setAttribute('exposure', '1.12');
        mv.setAttribute('ar', '');
        mv.setAttribute('ar-placement', 'wall');
        if (model.hostedSrc) {
          // Native AR intents need hosted files; blob src cannot use them.
          mv.setAttribute('ar-modes', 'scene-viewer quick-look webxr');
          if (self.cfg.modelUsdz) mv.setAttribute('ios-src', self.cfg.modelUsdz);
        } else {
          mv.setAttribute('ar-modes', 'webxr');
        }
        DBGlb.frameFront(mv); // hide until front-framed, reveal, then spin
        mount.innerHTML = '';
        mount.appendChild(mv);
      })
      .catch(function (err) {
        console.error('[db-configurator] 3D failed', err);
        mount.innerHTML = '<div class="db-3d__loading">3D preview is not available right now.</div>';
      });
  };

  DBConfigurator.prototype.close3d = function () {
    var panel = this.overlay.querySelector('[data-db-3d]');
    var mount = this.overlay.querySelector('[data-db-3d-mount]');
    panel.hidden = true;
    mount.innerHTML = '';
  };

  /* Layers that appear in the composition. forPrint=true also applies the
     Gelato "Sample element" rule: unchanged sample layers are NOT printed. */
  DBConfigurator.prototype.printedLayers = function (forPrint) {
    return (this.state.layers || []).filter(function (L) {
      if (!L.enabled || !L.text) return false;
      if (forPrint && L.sample && !L.changed) return false;
      return true;
    });
  };

  /* Highest-resolution design source for the PRINT file. */
  DBConfigurator.prototype.printDesignSrc = function () {
    if (this.state.uploadDataUrl) return this.state.uploadDataUrl;
    var src = this.state.designImage || this.cfg.designImageFull || this.cfg.designImage || '';
    if (!src) {
      var v = this.matchVariant();
      src = (v && v.image) || this.cfg.featuredImage || '';
    }
    src = String(src);
    if (src.indexOf('cdn.shopify.com') >= 0) {
      if (/[?&]width=\d+/.test(src)) src = src.replace(/([?&])width=\d+/, '$1width=4600');
      else src += (src.indexOf('?') >= 0 ? '&' : '?') + 'width=4600';
    }
    return src;
  };

  /* Print pixel size: ~150dpi target, capped for mobile canvas limits. */
  DBConfigurator.prototype.printPixelSize = function () {
    var cm = this.currentSizeCm();
    var W = Math.round(cm.w / 2.54 * 150);
    var H = Math.round(cm.h / 2.54 * 150);
    var k = Math.min(1, PRINT_MAX_SIDE / Math.max(W, H));
    W = Math.round(W * k); H = Math.round(H * k);
    var areaK = Math.sqrt(Math.min(1, PRINT_MAX_AREA / (W * H)));
    return { w: Math.max(600, Math.round(W * areaK)), h: Math.max(600, Math.round(H * areaK)) };
  };

  /* Core compositor. opts:
     - raw:      true = design + text only (3D texture / print file); false =
                 painted frame + mat (cart thumbnail mockup)
     - px:       long-side pixels (default 1024)
     - forPrint: print resolution + hi-res design + sample-element rule       */
  /* Canvas products are stretched over a frame: Gelato's print area covers the
     front PLUS the wrap around the bars. A file cut to the front alone loses
     its outer band to the wrap (that is what cropped "YOUR DREAMS"). Posters
     have no wrap, which is why they always looked right. So for canvas we grow
     the print file by a bleed margin and fill it with mirrored artwork — the
     wrap then eats mirror, never the design. */
  DBConfigurator.prototype.canvasBleedFraction = function () {
    if (!/canvas|tela/i.test(this.state.material || '')) return 0;
    var cm = this.currentSizeCm();
    var shortSide = Math.min(cm.w, cm.h) || 30;
    return Math.min(0.16, Math.max(0.08, 3.5 / shortSide)); // ~3.5 cm of wrap
  };

  DBConfigurator.prototype.composeCanvas = function (opts) {
    opts = opts || {};
    var self = this;
    var cm = this.currentSizeCm();
    var W, H;
    if (opts.forPrint) {
      var pp = this.printPixelSize();
      W = pp.w; H = pp.h;
    } else {
      var px = opts.px || 1024;
      var ratio = cm.h / cm.w;
      W = ratio >= 1 ? Math.round(px / ratio) : px;
      H = ratio >= 1 ? px : Math.round(px * ratio);
    }
    var src = opts.forPrint ? this.printDesignSrc() : this.previewImage();

    var load = DBGlb.loadImage(src);
    if (opts.forPrint && src !== this.previewImage()) {
      // Hi-res design may fail (huge R2 master on mobile): fall back to the
      // web-optimized preview source rather than losing the sale.
      load = load.catch(function () { return DBGlb.loadImage(self.previewImage()); });
    }

    // Wrap bleed: only on the real PRINT file (the mockup/preview must keep
    // showing exactly the front face the customer is buying).
    var bleed = opts.forPrint ? this.canvasBleedFraction() : 0;
    if (bleed > 0) {
      W = Math.round(W * (1 + bleed * 2));
      H = Math.round(H * (1 + bleed * 2));
    }

    return load.then(function (img) {
      var canvas = document.createElement('canvas');
      canvas.width = W;
      canvas.height = H;
      var ctx = canvas.getContext('2d');

      // raw = texture for 3D / print: the frame is real geometry (or a real
      // physical frame), so we bake only design + border + text, NOT a painted
      // frame. Non-raw paints the frame for the cart-thumbnail mockup.
      var frameHex = self.currentFrameHex();
      var hasFrame = !opts.raw && !opts.noBorder && frameHex && frameHex !== 'none';
      var framePx = hasFrame ? Math.round(W * 0.035) : 0;
      var matPct = opts.noBorder ? 0 : self.currentBorderPct() / 100;
      var matPx = Math.round(Math.min(W, H) * matPct);
      var bg = self.state.bgColor ? safeColor(self.state.bgColor) : '#f7f5f0';

      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);
      if (hasFrame) {
        ctx.fillStyle = frameHex;
        ctx.fillRect(0, 0, W, H);
      }
      if (matPx > 0 || hasFrame) {
        ctx.fillStyle = bg;
        ctx.fillRect(framePx, framePx, W - framePx * 2, H - framePx * 2);
      }
      // With canvas bleed the artwork stays inside the FRONT rectangle; the
      // bleed ring around it is filled by the mirrored bands.
      var bx = bleed > 0 ? Math.round(W * bleed / (1 + bleed * 2)) : 0;
      var by = bleed > 0 ? Math.round(H * bleed / (1 + bleed * 2)) : 0;
      var ax = framePx + matPx + bx, ay = framePx + matPx + by;
      var aw = W - ax * 2, ah = H - ay * 2;
      // contain + mirrored bands: the design is never cropped (see drawDesignMeet)
      drawDesignMeet(ctx, img, ax, ay, aw, ah);
      if (bleed > 0) {
        // extend the artwork outward into the wrap ring, mirrored on each side
        ctx.save();
        ctx.beginPath();
        ctx.rect(0, 0, W, H);
        ctx.clip();
        ctx.save(); ctx.translate(ax, 0); ctx.scale(-1, 1);
        ctx.drawImage(canvas, ax, ay, aw, ah, 0, ay, aw, ah); ctx.restore();
        ctx.save(); ctx.translate(ax + aw, 0); ctx.scale(-1, 1);
        ctx.drawImage(canvas, ax, ay, aw, ah, -aw, ay, aw, ah); ctx.restore();
        ctx.save(); ctx.translate(0, ay); ctx.scale(1, -1);
        ctx.drawImage(canvas, 0, ay, W, ah, 0, 0, W, ah); ctx.restore();
        ctx.save(); ctx.translate(0, ay + ah); ctx.scale(1, -1);
        ctx.drawImage(canvas, 0, ay, W, ah, 0, -ah, W, ah); ctx.restore();
        ctx.restore();
      }

      var stacks = { top: 0, center: 0, bottom: 0 };
      self.printedLayers(!!opts.forPrint).forEach(function (L) {
        var px2 = Math.round(W * 0.055 * (L.sizeMul || 1));
        ctx.fillStyle = safeColor(L.color);
        ctx.textAlign = 'center';
        ctx.font = fontCss(L.font, px2);
        ctx.shadowColor = 'rgba(0,0,0,0.4)';
        ctx.shadowBlur = Math.round(W * 0.012);
        var stack = Math.min(stacks[L.pos] || 0, 2);
        stacks[L.pos] = (stacks[L.pos] || 0) + 1;
        var frac = L.pos === 'top' ? 0.12 + stack * 0.09
          : L.pos === 'center' ? 0.52 + (stack === 1 ? 0.1 : stack === 2 ? -0.1 : 0)
            : 0.92 - stack * 0.09;
        ctx.fillText(L.text, W / 2, ay + ah * frac, aw * 0.9);
      });

      return canvas;
    });
  };

  /* Back-compat wrapper used by the 3D viewer (PNG bytes for the GLB texture). */
  DBConfigurator.prototype.composeTexture = function (opts) {
    opts = opts || {};
    return this.composeCanvas({ raw: opts.raw, px: 1024 }).then(function (canvas) {
      return DBGlb.canvasToPngBytes(canvas);
    });
  };

  DBConfigurator.prototype.composeBlob = function (opts) {
    return this.composeCanvas(opts).then(function (canvas) {
      return new Promise(function (resolve, reject) {
        canvas.toBlob(function (blob) {
          if (!blob) { reject(new Error('toBlob failed')); return; }
          resolve(blob);
        }, 'image/jpeg', opts.quality || 0.9);
      });
    });
  };

  customElements.define('db-configurator', DBConfigurator);
})();
